import { h } from '@omni-torn/ui';
import {
  bankGiveLink,
  cleanPayoutRules,
  decodePayoutRules,
  DEFAULT_PAYOUT_RULES,
  encodePayoutRules,
  PAYOUT_BASES,
  tornWarReportUrl,
  payoutCsvRows,
  payouts,
  WAR_RECORDS_TERMS,
  warHighlights,
  warLedger,
  type FactionWarAnswer,
  type FactionWarsAnswer,
  type PayoutRules,
  type WarListEntry,
  type WarRecord,
} from '@omni-torn/shared';
import { server } from '../session';
import { csvButton } from '../log-kit/ui';
import { ago, box, ext, facts, kids, loading, money, num, problem, profileUrl, table } from '../ui';

/**
 * Faction → Wars (website roadmap S21: Torn Intel's payouts, TornBot's payout calculator, TornStats' wars/{id}): your
 * faction's finished ranked wars member by member — Torn's report of both sides with each member's war hits and score,
 * and from the faction's attack log their respect, fair fight, assists, losses, hits outside the war and hits taken —
 * a payout calculator (in this browser: per hit, per respect, per score or equal shares, a pool or a rate, the faction's
 * cut, minimum hits, minimum and maximum payout, a bonus for the top members) with a per-member report, CSV and Torn's own
 * Give form for bankers, a link to share the payout with faction members (no public links), and the ledger across wars.
 *
 * From the server's /api/faction/wars* (read with the keys members gave the Discord bot) — kept for the faction from terms
 * version 10, else an hour. The faction page draws itself again every 20 s: this tab is built once per visit (kept below).
 */

let built: HTMLElement | null = null;
/** A new visit to the Faction page starts afresh. */
export function resetWars(): void {
  built = null;
}

const day = (sec: number) => new Date(sec * 1000).toISOString().slice(0, 10);
const resultOf = (w: Pick<WarListEntry, 'end' | 'winner' | 'us'>) => (!w.end ? h('span', {}, 'going on') : w.winner ? (w.winner === w.us.id ? h('span', { class: 't-green' }, 'won') : h('span', { class: 'st-red' }, 'lost')) : h('span', {}, 'ended'));

// ---------------------------------------------------------------- the payout rules (this browser; a shared link wins)

const RULES_KEY = 'omni-torn.war-payouts';
function loadRules(): PayoutRules {
  try {
    return cleanPayoutRules(JSON.parse(localStorage.getItem(RULES_KEY) ?? 'null'));
  } catch {
    return { ...DEFAULT_PAYOUT_RULES };
  }
}
function saveRules(r: PayoutRules): void {
  try {
    localStorage.setItem(RULES_KEY, JSON.stringify(r));
  } catch {
    /* this visit only */
  }
}

/** The war and payout from a shared link (/faction?war=…&pay=…#wars). */
function fromLink(): { war: number | null; rules: PayoutRules | null } {
  const q = new URLSearchParams(location.search);
  const war = Number(q.get('war'));
  return { war: Number.isSafeInteger(war) && war > 0 ? war : null, rules: q.has('pay') ? decodePayoutRules(q.get('pay')) : null };
}

/** The link that opens this war with these payout rules (only members of the faction, signed in, see anything). */
export function warShareLink(warId: number, rules: PayoutRules): string {
  const pay = encodePayoutRules(rules);
  return `/faction?war=${warId}${pay ? `&pay=${encodeURIComponent(pay)}` : ''}#wars`;
}

// ---------------------------------------------------------------- the tab

/** The Wars tab (built once per visit). */
export function warsSection(): HTMLElement[] {
  if (!built) built = makeWars();
  return [built];
}

function makeWars(): HTMLElement {
  const link = fromLink();
  let rules = link.rules ?? loadRules();
  let picked: number | null = link.war;
  let list: FactionWarsAnswer | null = null;
  const records = new Map<number, FactionWarAnswer>();
  const listHolder = h('div', {}, loading('Reading your faction’s ranked wars…'));
  const warHolder = h('div', {});
  const payHolder = h('div', {});
  const ledgerHolder = h('div', {});
  let again: ReturnType<typeof setTimeout> | null = null;

  const keptNote = (a: FactionWarsAnswer) =>
    h(
      'p',
      { class: 'muted small', 'data-wars': 'kept' },
      a.readAt ? `Read from Torn ${ago(a.readAt)} with a key a member gave the Discord bot. ` : '',
      a.kept
        ? a.keepDays === 0
          ? 'Kept by Omni-Torn for your faction, with no end date.'
          : `Kept by Omni-Torn for your faction (up to ${num(a.keepDays)} days after each war).`
        : `Only what was read in the last hour. Omni-Torn keeps your faction’s war records once a member whose key the bot uses agrees to terms version ${a.terms}.`,
    );

  const drawList = () => {
    if (!list) return;
    listHolder.replaceChildren(
      ...kids(
        list.problem ? h('p', { class: 'notice', 'data-wars': 'problem' }, list.problem) : null,
        table(
          list.wars,
          [
            { label: 'Against', get: (w) => ext(`https://www.torn.com/factions.php?step=profile&ID=${w.enemy.id}`, w.enemy.name), sort: (w) => w.enemy.name.toLowerCase() },
            { label: 'Result', get: (w) => resultOf(w) },
            { label: 'Score', get: (w) => `${num(w.us.score)} – ${num(w.enemy.score)}`, sort: (w) => w.us.score - w.enemy.score, cls: 'r' },
            { label: 'When', get: (w) => (w.end ? `${day(w.start)} – ${day(w.end)}` : `since ${day(w.start)}`), sort: (w) => w.start },
            { label: '', get: (w) => (w.kept ? h('span', { class: 'chip', title: 'Kept by Omni-Torn for your faction' }, w.counted ? 'kept · counted' : 'kept') : w.counted ? h('span', { class: 'chip' }, 'counted') : '') },
            {
              label: '',
              get: (w) => {
                if (!w.end) return h('span', { class: 'muted small' }, 'the War tab follows it');
                const b = h('button', { type: 'button', class: 'btn-small', 'data-war-pick': String(w.id) }, picked === w.id ? 'Shown' : 'Open') as HTMLButtonElement;
                b.disabled = picked === w.id;
                b.addEventListener('click', () => pick(w.id));
                return b;
              },
              cls: 'r',
            },
          ],
          { initial: 3, empty: 'Torn lists no ranked wars for your faction.', remember: 'wars-list' },
        ),
        keptNote(list),
      ),
    );
  };

  const readList = () =>
    server<FactionWarsAnswer>('GET', '/api/faction/wars')
      .then((a) => {
        list = a;
        // The war from a shared link, else the newest finished one.
        const first = picked ?? a.wars.find((w) => w.end)?.id ?? null;
        if (first !== null) pick(first);
        else drawList();
        drawLedger();
      })
      .catch((err) => listHolder.replaceChildren(problem(err)));

  /** Ask for a war's record; while the attack log is still being counted, ask again a little later (only while in view). */
  const readWar = (id: number) =>
    server<FactionWarAnswer>('GET', `/api/faction/wars/${id}`)
      .then((a) => {
        records.set(id, a);
        if (picked === id) drawWar();
        drawLedger();
        if (a.counting && picked === id) {
          if (again) clearTimeout(again);
          again = setTimeout(() => {
            if (built?.isConnected && document.visibilityState === 'visible' && picked === id) void readWar(id);
          }, 15_000);
        }
      })
      .catch((err) => {
        if (picked === id) warHolder.replaceChildren(problem(err));
      });

  function pick(id: number): void {
    picked = id;
    if (again) clearTimeout(again);
    again = null;
    drawList();
    const had = records.get(id);
    if (had) drawWar();
    else {
      warHolder.replaceChildren(loading('Reading the war’s report…'));
      payHolder.replaceChildren();
    }
    if (!had || had.counting) void readWar(id);
  }

  // ---------------------------------------------------------------- one war

  function drawWar(): void {
    const a = picked === null ? null : records.get(picked);
    if (!a) return;
    const w = a.war;
    if (!w) {
      warHolder.replaceChildren(h('p', { class: 'notice', 'data-wars': 'war-problem' }, a.problem ?? 'Torn has no report of that war.'));
      payHolder.replaceChildren();
      return;
    }
    const counted = w.log !== null;
    const status = w.counted
      ? `Counted from your faction’s attack log (${num(w.log!.lines)} lines).`
      : a.counting
        ? `Counting your faction’s attack log… (${num(w.log?.lines ?? 0)} lines so far — this fills in by itself).`
        : 'Respect, fair fight, assists, outside hits and hits taken come from your faction’s attack log.';
    const rewards = w.us.rewards;
    warHolder.replaceChildren(
      ...kids(
        facts([
          ['Result', h('span', { 'data-wars': 'result' }, resultOf({ end: w.end, winner: w.winner, us: w.us }), ` ${num(w.us.score)} – ${num(w.enemy.score)} against ${w.enemy.name}${w.forfeit ? ' (forfeit)' : ''}`)],
          ['When', `${day(w.start)} – ${day(w.end)} (${num(Math.round((w.end - w.start) / 3600))} hours)`],
          ['Hits', `${num(w.us.attacks)} by us · ${num(w.enemy.attacks)} by them`],
          ...(w.us.rank && (w.us.rank.before || w.us.rank.after) ? ([['Rank', `${w.us.rank.before ?? '?'} → ${w.us.rank.after ?? '?'}`]] as Array<[string, string]>) : []),
          ...(rewards ? ([['Rewards', [rewards.respect ? `${num(rewards.respect)} respect` : '', rewards.points ? `${num(rewards.points)} points` : '', ...rewards.items.map((i) => `${num(i.quantity)}× ${i.name}`)].filter(Boolean).join(' · ') || '—']] as Array<[string, string]>) : []),
        ]),
        h('p', { class: 'muted small', 'data-wars': 'log' }, status, ' ', ext(tornWarReportUrl(w.id), 'Torn’s own report ↗')),
        a.problem ? h('p', { class: 'notice', 'data-wars': 'war-problem' }, a.problem) : null,
        table(
          w.members,
          [
            { label: 'Member', get: (m) => ext(profileUrl(m.id), m.name), sort: (m) => m.name.toLowerCase() },
            { label: 'Lvl', get: (m) => num(m.level), sort: (m) => m.level ?? 0, cls: 'r' },
            { label: 'Hits', get: (m) => num(m.hits), sort: (m) => m.hits, cls: 'r' },
            { label: 'Score', get: (m) => num(m.score, 2), sort: (m) => m.score, cls: 'r' },
            ...(counted
              ? [
                  { label: 'Respect', get: (m: WarRecord['members'][number]) => num(m.respect, 2), sort: (m: WarRecord['members'][number]) => m.respect ?? -1, cls: 'r' },
                  { label: 'Avg FF', get: (m: WarRecord['members'][number]) => (m.avgFairFight === null ? '·' : m.avgFairFight.toFixed(2)), sort: (m: WarRecord['members'][number]) => m.avgFairFight ?? -1, cls: 'r' },
                  { label: 'Assists', get: (m: WarRecord['members'][number]) => num(m.assists), sort: (m: WarRecord['members'][number]) => m.assists ?? -1, cls: 'r' },
                  { label: 'Lost', get: (m: WarRecord['members'][number]) => num(m.losses), sort: (m: WarRecord['members'][number]) => m.losses ?? -1, cls: 'r' },
                  { label: 'Outside', get: (m: WarRecord['members'][number]) => num(m.outside), sort: (m: WarRecord['members'][number]) => m.outside ?? -1, cls: 'r' },
                  { label: 'Hit by them', get: (m: WarRecord['members'][number]) => num(m.bled), sort: (m: WarRecord['members'][number]) => m.bled ?? -1, cls: 'r' },
                ]
              : []),
          ],
          { initial: 3, remember: 'wars-members', csv: false },
        ),
        h(
          'details',
          { 'data-wars': 'enemy' },
          h('summary', {}, `${w.enemy.name}: ${num(w.enemy.score)} score · ${num(w.enemyMembers.length)} member${w.enemyMembers.length === 1 ? '' : 's'} (Torn’s report)`),
          table(
            w.enemyMembers,
            [
              { label: 'Member', get: (m) => ext(profileUrl(m.id), m.name), sort: (m) => m.name.toLowerCase() },
              { label: 'Lvl', get: (m) => num(m.level), sort: (m) => m.level ?? 0, cls: 'r' },
              { label: 'Hits', get: (m) => num(m.attacks), sort: (m) => m.attacks, cls: 'r' },
              { label: 'Score', get: (m) => num(m.score, 2), sort: (m) => m.score, cls: 'r' },
            ],
            { initial: 3, remember: 'wars-enemy' },
          ),
        ),
        h(
          'div',
          { class: 'calc-row' },
          csvButton(
            () => `war-${w.id}-members.csv`,
            () => ({
              header: ['Member', 'ID', 'Level', 'War hits', 'Score', 'Respect', 'Avg FF', 'Assists', 'Lost', 'Outside hits', 'Hit by enemy'],
              rows: w.members.map((m) => [m.name, m.id, m.level, m.hits, m.score, m.respect, m.avgFairFight, m.assists, m.losses, m.outside, m.bled]),
            }),
            'Members as CSV',
          ),
        ),
      ),
    );
    drawPayouts(w);
  }

  // ---------------------------------------------------------------- payouts

  /** The form (kept between draws so typing isn't lost). */
  let form: HTMLElement | null = null;
  const result = h('div', { 'data-wars': 'payout-result' });
  let current: WarRecord | null = null;

  function field(label: string, key: keyof PayoutRules, opts: { step?: string; max?: string; title?: string; wide?: boolean } = {}): HTMLElement {
    const i = h('input', { type: 'number', min: '0', step: opts.step ?? '1', ...(opts.max ? { max: opts.max } : {}), 'data-pay': key, value: String(rules[key] || ''), placeholder: '0', ...(opts.title ? { title: opts.title } : {}) }) as HTMLInputElement;
    // Money fields hold billions.
    if (opts.wide) i.style.width = '140px';
    i.addEventListener('input', () => {
      rules = cleanPayoutRules({ ...rules, [key]: i.value === '' ? 0 : i.value });
      saveRules(rules);
      showPayout();
    });
    return h('label', { class: 'target-field' }, label, i);
  }

  function makeForm(): HTMLElement {
    const basis = h('select', { 'data-pay': 'basis', 'aria-label': 'Pay by' }, ...PAYOUT_BASES.map((b) => h('option', { value: b.id }, b.label))) as HTMLSelectElement;
    basis.value = rules.basis;
    const mode = h('select', { 'data-pay': 'mode', 'aria-label': 'How' }, h('option', { value: 'pool' }, 'Share a pool'), h('option', { value: 'rate' }, 'A set amount each')) as HTMLSelectElement;
    mode.value = rules.mode;
    const poolOnly = h('div', { class: 'target-fields' }, field('Pool ($)', 'pool', { title: 'The money to share out', wide: true }), field('Faction keeps (%)', 'factionCut', { max: '100', step: '0.5' }));
    const rateOnly = h('div', { class: 'target-fields' }, field('$ per hit / respect / score point', 'rate', { step: 'any', wide: true }));
    const showMode = () => {
      // (.target-fields is a flex box: `hidden` alone wouldn't hide it.)
      poolOnly.style.display = rules.mode === 'pool' ? '' : 'none';
      rateOnly.style.display = rules.mode === 'rate' ? '' : 'none';
    };
    basis.addEventListener('change', () => {
      rules = cleanPayoutRules({ ...rules, basis: basis.value });
      saveRules(rules);
      showPayout();
    });
    mode.addEventListener('change', () => {
      rules = cleanPayoutRules({ ...rules, mode: mode.value });
      saveRules(rules);
      showMode();
      showPayout();
    });
    showMode();
    return h(
      'div',
      { class: 'war-filters', 'data-keep-focus': '' },
      h('div', { class: 'target-fields' }, h('label', { class: 'target-field' }, 'Pay by', basis), h('label', { class: 'target-field' }, 'How', mode)),
      poolOnly,
      rateOnly,
      h(
        'div',
        { class: 'target-fields' },
        field('Min war hits', 'minHits'),
        field('An assist counts as (hits)', 'assistValue', { step: '0.1', max: '1', title: 'Only once the attack log is counted' }),
        field('Min payout ($)', 'minPay', { wide: true }),
        field('Max payout ($)', 'maxPay', { title: '0: no cap', wide: true }),
        field('Top bonus ($)', 'topBonus', { wide: true }),
        field('…for the top', 'topCount'),
      ),
    );
  }

  function showPayout(): void {
    const w = current;
    if (!w) return;
    const res = payouts(w.members, rules);
    const share = h('span', { class: 'share' });
    const shareBtn = h('button', { type: 'button', class: 'btn-small', 'data-wars': 'share' }, 'Copy a link for your faction') as HTMLButtonElement;
    shareBtn.addEventListener('click', () => {
      const url = `${location.origin}${warShareLink(w.id, rules)}`;
      const note = 'Only members of your faction signed in to Omni-Torn see the war; the link carries just these payout settings.';
      const shown = () => share.replaceChildren(h('input', { type: 'text', readonly: true, value: url, class: 'share-link', 'aria-label': 'Link to copy', 'data-wars': 'share-link' }), ` ${note}`);
      if (!navigator.clipboard) return shown();
      navigator.clipboard.writeText(url).then(
        () => {
          shown();
          share.append(' Copied.');
        },
        () => shown(),
      );
    });
    result.replaceChildren(
      ...kids(
        h(
          'p',
          { 'data-wars': 'payout-total' },
          h('strong', {}, `${money(res.total)} to ${num(res.rows.filter((r) => r.pay > 0).length)} member${res.rows.filter((r) => r.pay > 0).length === 1 ? '' : 's'}`),
          rules.mode === 'pool' ? ` · the faction keeps ${money(res.kept)}` : '',
        ),
        res.notes.length ? h('p', { class: 'notice', 'data-wars': 'payout-notes' }, res.notes.join(' ')) : null,
        table(
          res.rows,
          [
            { label: 'Member', get: (r) => ext(profileUrl(r.id), r.name), sort: (r) => r.name.toLowerCase() },
            { label: PAYOUT_BASES.find((b) => b.id === rules.basis)!.label, get: (r) => (r.qualified ? num(r.units, 2) : h('span', { class: 'muted', title: `Fewer than ${rules.minHits} war hits` }, num(r.units, 2))), sort: (r) => r.units, cls: 'r' },
            { label: 'Share', get: (r) => `${(r.share * 100).toFixed(1)}%`, sort: (r) => r.share, cls: 'r' },
            { label: 'Bonus', get: (r) => (r.bonus ? money(r.bonus) : '·'), sort: (r) => r.bonus, cls: 'r' },
            { label: 'Payout', get: (r) => h('span', { title: r.clamp === 'max' ? 'Cut to the maximum' : r.clamp === 'min' ? 'Raised to the minimum' : '' }, money(r.pay), r.clamp ? (r.clamp === 'max' ? ' ↓' : ' ↑') : ''), sort: (r) => r.pay, cls: 'r' },
            { label: '', get: (r) => (r.pay > 0 ? h('a', { href: bankGiveLink(r.id, r.pay), target: '_blank', rel: 'noopener', 'data-give': String(r.id), title: 'For bankers: Torn’s own Give to user form, filled in (with the extension) — you press Torn’s button' }, 'Give on Torn') : ''), cls: 'r' },
          ],
          { initial: 4, remember: 'wars-payouts', csv: false },
        ),
        h(
          'div',
          { class: 'calc-row' },
          csvButton(
            () => `war-${w.id}-payouts.csv`,
            () => payoutCsvRows(w, payouts(w.members, rules)),
            'Payouts as CSV',
          ),
          shareBtn,
          share,
        ),
        h('p', { class: 'muted small' }, 'Worked out in your browser — nothing is sent or paid. Bankers pay on Torn: “Give on Torn” opens Torn’s own form, filled in by the extension, and Torn’s button sends it.'),
      ),
    );
  }

  function drawPayouts(w: WarRecord): void {
    current = w;
    if (!form) form = makeForm();
    showPayout();
    payHolder.replaceChildren(form, result);
  }

  // ---------------------------------------------------------------- across wars

  function drawLedger(): void {
    const recs = [...records.values()].map((a) => a.war).filter((w): w is WarRecord => !!w);
    const reported = list?.wars.filter((w) => w.end && w.reported && !records.has(w.id)) ?? [];
    const btn = h('button', { type: 'button', class: 'btn-small', 'data-wars': 'ledger-more' }, `Add the ${num(Math.min(reported.length, 30))} other war${reported.length === 1 ? '' : 's'} already read`) as HTMLButtonElement;
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      // One at a time (each is already read: no Torn calls for kept ones).
      for (const w of reported.slice(0, 30)) await readWar(w.id).catch(() => {});
      drawLedger();
    });
    if (!recs.length) {
      ledgerHolder.replaceChildren(h('p', { class: 'muted' }, 'Open a war to start.'));
      return;
    }
    const l = warLedger(recs);
    const latest = [...recs].sort((a, b) => b.start - a.start)[0] ?? null;
    const hl = warHighlights(l, latest);
    ledgerHolder.replaceChildren(
      ...kids(
        h('p', { class: 'muted small', 'data-wars': 'ledger-count' }, `Over ${num(recs.length)} war${recs.length === 1 ? '' : 's'}. `, reported.length ? btn : ''),
        hl.length ? h('ul', { class: 'plain-list', 'data-wars': 'highlights' }, ...hl.map((x) => h('li', {}, h('strong', {}, `${x.label}: `), `${x.name} — ${x.why}`))) : null,
        table(
          l,
          [
            { label: 'Member', get: (m) => ext(profileUrl(m.id), m.name), sort: (m) => m.name.toLowerCase() },
            { label: 'Wars', get: (m) => num(m.wars), sort: (m) => m.wars, cls: 'r' },
            { label: 'Avg hits', get: (m) => num(m.avgHits, 1), sort: (m) => m.avgHits, cls: 'r' },
            { label: 'Last 5', get: (m) => num(m.recentAvg, 1), sort: (m) => m.recentAvg, cls: 'r' },
            { label: 'Took part', get: (m) => `${Math.round(m.participation * 100)}%`, sort: (m) => m.participation, cls: 'r' },
            { label: 'Rating', get: (m) => h('span', { title: `60 for showing up, up to 40 for hits against the average member, −10 a war in a row without a hit (${m.confidence} confidence)` }, `${m.grade} ${m.rating}`), sort: (m) => m.rating, cls: 'r' },
            { label: 'Form', get: (m) => m.form, sort: (m) => m.form },
            { label: '', get: (m) => (m.flags.length ? h('span', { class: 'st-red small' }, m.flags.join(', ')) : '') },
          ],
          { initial: 5, remember: 'wars-ledger' },
        ),
      ),
    );
  }

  void readList();
  return h(
    'div',
    { 'data-wars': 'tab' },
    box('Ranked wars', listHolder, { id: 'wars-list' }),
    box('The war, member by member', warHolder, { id: 'war-record' }),
    box('Payouts', payHolder, { id: 'war-payouts' }),
    box('Across wars', ledgerHolder, { id: 'war-ledger' }),
    h('p', { class: 'muted small' }, `War records are read with keys members gave the Discord bot (your own faction only) and are for your faction’s members only. Kept from terms version ${WAR_RECORDS_TERMS}.`),
  );
}
